(() => {
  "use strict";

  const qs = (root, selector) => root.querySelector(selector);
  const qsa = (root, selector) => Array.from(root.querySelectorAll(selector));

  function quantityValue(studio) {
    const input = qs(studio, "[data-quantity]");
    const value = input ? Math.floor(Number(input.value)) : 1;
    return value > 0 ? value : 1;
  }

  function priceAmount(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return 0;
    return String(value).includes(".") ? amount : amount / 100;
  }

  function moneyAmount(studio, amount) {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: studio.data.currency || "CAD",
    }).format(amount);
  }

  function priceForQuantity(studio, variant) {
    return variant ? moneyAmount(studio, priceAmount(variant.price) * quantityValue(studio)) : "Unavailable";
  }

  function updateDisplayedPrice(studio, variant) {
    const price = qs(studio, "[data-price]");
    if (price) price.textContent = priceForQuantity(studio, variant);

    const label = qs(studio, ".print-studio__summary-main span") || qs(studio, ".print-studio__summary > span");
    if (label) label.textContent = quantityValue(studio) > 1 ? "Selected total" : "Selected price";
  }

  function sizeParts(size) {
    const parts = String(size || "").toLowerCase().split("x").map((part) => Number(part.trim()));
    return parts.length === 2 && parts[0] && parts[1] ? parts : null;
  }

  function orientationValue(studio) {
    return studio.printOrientation === "landscape" ? "landscape" : "portrait";
  }

  function orientationLabel(studio) {
    return orientationValue(studio) === "landscape" ? "Landscape" : "Portrait";
  }

  function displaySize(studio, size) {
    const parts = sizeParts(size);
    if (!parts) return size || "";
    const first = Math.min(parts[0], parts[1]);
    const second = Math.max(parts[0], parts[1]);
    if (first === second) return first + "x" + second;
    return orientationValue(studio) === "landscape" ? second + "x" + first : first + "x" + second;
  }

  function sizeKeyFromParts(parts) {
    if (!parts) return "";
    return Math.min(parts[0], parts[1]) + "x" + Math.max(parts[0], parts[1]);
  }

  function sizeKey(size) {
    return sizeKeyFromParts(sizeParts(size));
  }

  function parseArtworkSizeFromName(name) {
    const match = String(name || "").match(/(\d+(?:\.\d+)?)\s*(?:x|by)\s*(\d+(?:\.\d+)?)/i);
    if (!match) return null;

    const width = Number(match[1]);
    const height = Number(match[2]);
    return width > 0 && height > 0 ? [width, height] : null;
  }

  function parseArtworkSizeFromPixels(image) {
    if (!image || !image.naturalWidth || !image.naturalHeight) return null;

    const width = image.naturalWidth / 300;
    const height = image.naturalHeight / 300;
    const roundedWidth = Math.round(width * 10) / 10;
    const roundedHeight = Math.round(height * 10) / 10;
    const closeToTenths = Math.abs(width - roundedWidth) < 0.05 && Math.abs(height - roundedHeight) < 0.05;
    return closeToTenths ? [roundedWidth, roundedHeight] : null;
  }

  function availableSizeMatch(studio, parts) {
    const targetKey = sizeKeyFromParts(parts);
    if (!targetKey || typeof studio.sizes !== "function") return "";

    return (studio.sizes() || []).find((size) => sizeKey(size) === targetKey) || "";
  }

  function applyDetectedArtworkSize(studio, file, image) {
    const parts = parseArtworkSizeFromName(file && file.name) || parseArtworkSizeFromPixels(image);
    const matchedSize = availableSizeMatch(studio, parts);
    if (!parts || !matchedSize) return;

    studio.artRatio = parts[0] / parts[1];
    studio.printOrientation = parts[0] > parts[1] ? "landscape" : "portrait";
    studio.size = matchedSize;
    if (typeof studio.renderSizes === "function") studio.renderSizes();
    updateOrientationPicked(studio);
    updateSizeLabels(studio);
    if (typeof studio.updatePreview === "function") studio.updatePreview();
    if (typeof studio.sync === "function") studio.sync();
  }

  function orientedRatio(studio, size) {
    const parts = sizeParts(displaySize(studio, size));
    return parts ? parts[0] / parts[1] : 0;
  }

  function removeProofOption(studio) {
    qsa(studio, '.print-studio__proof,[data-proof],[data-prop="proof"],input[name="properties[Proof before production]"]').forEach((element) => {
      const wrapper = element.closest && element.closest(".print-studio__proof");
      (wrapper || element).remove();
    });
  }

  function ensureFrameVisualStyles() {
    if (document.getElementById("print-studio-frame-visuals")) return;

    const style = document.createElement("style");
    style.id = "print-studio-frame-visuals";
    style.textContent = `
      .print-studio {
        --canvas-edge: #d9d2c8;
        --canvas-side: #b8aea4;
        --floater-gap: clamp(4px, .55vw, 7px);
        --floater-rail: clamp(7px, .95vw, 11px);
        --print-border-x: 0%;
        --print-border-y: 0%;
        --print-border-inner-w: 100%;
        --print-border-inner-h: 100%;
      }

      .print-studio__mobile-progress {
        display: none;
      }

      .print-studio__frame {
        position: relative;
        transform-style: preserve-3d;
      }

      .print-studio__art img {
        transform: translate(var(--crop-x, 0%), var(--crop-y, 0%)) scale(var(--zoom, 1)) !important;
      }

      .print-studio[data-crop-mode="fill"] .print-studio__art img {
        object-fit: cover !important;
      }

      .print-studio[data-crop-mode="fit"] .print-studio__art img {
        object-fit: contain !important;
      }

      .print-studio[data-crop-mode^="border-"] .print-studio__art {
        background: #fff !important;
      }

      .print-studio[data-crop-mode^="border-"] .print-studio__art img {
        top: var(--print-border-y) !important;
        right: auto !important;
        bottom: auto !important;
        left: var(--print-border-x) !important;
        width: var(--print-border-inner-w) !important;
        height: var(--print-border-inner-h) !important;
        object-fit: contain !important;
        transform: none !important;
      }

      .print-studio__preview-controls {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        align-items: end;
      }

      .print-studio__preview-controls label {
        min-width: 0;
      }

      .print-studio__crop-mode,
      .print-studio__crop-reset {
        min-height: 42px;
        border: 1px solid var(--line);
        background: #fff;
        color: var(--ink);
        font: inherit;
        font-weight: 900;
        cursor: pointer;
      }

      .print-studio__crop-mode.is-picked {
        border-color: var(--ink);
        background: var(--ink);
        color: #fff;
      }

      .print-studio__crop-actions {
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        gap: 8px;
      }

      .print-studio__border-note {
        grid-column: 1 / -1;
        margin: 2px 0 0;
        color: #646970;
        font-size: .86rem;
        font-weight: 700;
        line-height: 1.35;
      }

      .print-studio__border-note[hidden] {
        display: none !important;
      }

      .print-studio__customer-note {
        display: grid;
        gap: 8px;
        margin-top: 12px;
        padding: 12px;
        border: 1px solid var(--line);
        background: #fff;
      }

      .print-studio__customer-note span {
        color: var(--ink);
        font-weight: 900;
      }

      .print-studio__customer-note textarea {
        width: 100%;
        min-height: 86px;
        padding: 10px 12px;
        border: 1px solid var(--line);
        background: #fff;
        color: var(--ink);
        font: inherit;
        resize: vertical;
      }

      .print-studio__customer-note small {
        color: #646970;
        line-height: 1.35;
      }

      @media (max-width: 700px) {
        .print-studio {
          display: block;
          min-height: 0;
          background: #f7f3ec;
        }

        .print-studio__scene,
        .print-studio.has-artwork .print-studio__scene {
          position: sticky;
          top: 0;
          z-index: 5;
          display: grid;
          height: clamp(138px, 23vh, 184px);
          min-height: 0;
          max-height: 184px;
          place-items: center;
          padding: 0;
          overflow: hidden;
          border-bottom: 1px solid rgba(32,34,41,.12);
          background: linear-gradient(180deg, #fbfaf7, #f2ede5);
          box-shadow: 0 8px 22px rgba(32,34,41,.08);
        }

        .print-studio.has-artwork .print-studio__scene {
          height: clamp(154px, 25vh, 210px);
          max-height: 210px;
        }

        .print-studio__wall,
        .print-studio.has-artwork .print-studio__wall {
          width: 100%;
          height: 100%;
          max-height: none;
          display: grid;
          place-items: center;
          background: transparent;
          box-shadow: none;
        }

        .print-studio__frame {
          width: clamp(78px, 25vw, 116px);
        }

        .print-studio.has-artwork .print-studio__frame {
          width: clamp(92px, 29vw, 136px);
        }

        .print-studio__ledge,
        .print-studio__steps,
        .print-studio__brand,
        .print-studio__kicker,
        .print-studio__intro {
          display: none !important;
        }

        .print-studio__content {
          display: grid;
          gap: 9px;
          padding: 10px 10px 96px;
        }

        .print-studio h1 {
          margin: 0;
          font-size: 1.42rem;
          line-height: 1.05;
        }

        .print-studio__mobile-progress {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 4px;
        }

        .print-studio__mobile-progress button {
          min-height: 34px;
          padding: 0 3px;
          border: 1px solid rgba(32,34,41,.16);
          background: rgba(255,255,255,.70);
          color: var(--ink);
          font: inherit;
          font-size: .68rem;
          font-weight: 900;
          line-height: 1.05;
        }

        .print-studio__mobile-progress button.is-active {
          border-color: var(--ink);
          background: var(--ink);
          color: #fff;
        }

        .print-studio__panel {
          padding: 11px;
          border: 1px solid rgba(32,34,41,.13);
          background: rgba(255,255,255,.78);
        }

        .print-studio__panel h2 {
          margin-bottom: 6px;
          font-size: 1rem;
        }

        .print-studio__panel p {
          margin-bottom: 10px;
          font-size: .86rem;
          line-height: 1.35;
        }

        .print-studio__upload {
          min-height: 92px;
          padding: 14px;
        }

        .print-studio__mobile-type-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }

        .print-studio__mobile-type {
          display: grid;
          gap: 5px;
          min-height: 86px;
          padding: 10px;
          border: 1px solid rgba(32,34,41,.16);
          background: #fff;
          text-align: left;
          font: inherit;
          cursor: pointer;
        }

        .print-studio__mobile-type strong {
          font-size: .9rem;
          line-height: 1.1;
        }

        .print-studio__popular-badge {
          display: inline-flex;
          align-items: center;
          width: max-content;
          padding: 4px 7px;
          border-radius: 999px;
          background: #fff1cf;
          color: #765000;
          font-size: .68rem;
          font-style: normal;
          font-weight: 950;
          line-height: 1;
          text-transform: uppercase;
          letter-spacing: 0;
        }

        .print-studio__mobile-type small {
          color: var(--muted);
          font-size: .72rem;
          line-height: 1.25;
        }

        .print-studio__mobile-type.is-picked {
          border-color: var(--ink);
          box-shadow: inset 0 0 0 2px var(--ink);
        }

        .print-studio__choice.is-mobile-incompatible {
          display: none;
        }

        .print-studio__mobile-crop {
          display: grid;
          gap: 8px;
          margin-top: 10px;
        }

        .print-studio__mobile-crop:empty {
          display: none;
        }

        .print-studio__panel .print-studio__preview-controls {
          position: static;
          display: grid;
          grid-template-columns: 1fr;
          gap: 8px;
          padding: 10px;
          border: 1px solid rgba(32,34,41,.12);
          background: #fff;
          backdrop-filter: none;
        }

        .print-studio__panel .print-studio__preview-controls label {
          gap: 4px;
          font-size: .76rem;
        }

        .print-studio__preview-controls {
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
          padding: 10px 12px 12px;
        }

        .print-studio__preview-controls label {
          gap: 5px;
          font-size: .78rem;
        }

        .print-studio__crop-actions {
          grid-column: 1 / -1;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 6px;
        }

        .print-studio__crop-mode,
        .print-studio__crop-reset {
          min-height: 36px;
          padding: 0 6px;
          font-size: .76rem;
          line-height: 1.1;
        }

        .print-studio__border-note {
          margin-top: 0;
          font-size: .75rem;
          line-height: 1.3;
        }

        .print-studio__customer-note {
          margin-top: 8px;
          padding: 10px;
        }

        .print-studio__customer-note textarea {
          min-height: 72px;
        }

        .print-studio__sizes {
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 6px;
          max-height: 36vh;
        }

        .print-studio__size {
          min-height: 52px;
          padding: 7px 4px;
        }

        .print-studio__grid {
          grid-template-columns: 1fr;
          gap: 8px;
        }

        .print-studio__segmented,
        .print-studio__depths {
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }

        .print-studio__swatches {
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
        }

        .print-studio__choice,
        .print-studio__mode,
        .print-studio__depth,
        .print-studio__swatch {
          padding: 10px;
        }

        .print-studio:not(.is-mobile-review) .print-studio__summary {
          display: none;
        }

        .print-studio__summary {
          padding: 9px 10px;
        }

        .print-studio__summary p {
          display: none;
        }

        .print-studio__actions {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 2147483000;
          display: grid;
          grid-template-columns: minmax(86px, .34fr) 1fr;
          gap: 8px;
          margin: 0;
          padding: 8px 10px calc(8px + env(safe-area-inset-bottom));
          background: rgba(247,243,236,.96);
          box-shadow: 0 -12px 28px rgba(32,34,41,.14);
          backdrop-filter: blur(16px);
        }

        .print-studio__actions button {
          min-height: 44px;
          padding: 0 12px;
        }
      }

      .print-studio__frame.is-canvas,
      .print-studio__frame.is-floater {
        position: relative;
        overflow: visible;
      }

      .print-studio__frame.is-canvas {
        padding: 0;
        background: transparent;
        filter: drop-shadow(0 24px 34px rgba(28,22,18,.18));
        box-shadow: none;
      }

      .print-studio__frame.is-canvas::before,
      .print-studio__frame.is-canvas::after {
        content: "";
        position: absolute;
        z-index: -1;
        pointer-events: none;
      }

      .print-studio__frame.is-canvas::before {
        top: 3px;
        right: -5px;
        bottom: -4px;
        width: 5px;
        background: linear-gradient(90deg, var(--canvas-side), var(--canvas-edge));
        transform: skewY(24deg);
        transform-origin: left top;
      }

      .print-studio__frame.is-canvas::after {
        left: 4px;
        right: -5px;
        bottom: -5px;
        height: 5px;
        background: linear-gradient(180deg, var(--canvas-edge), var(--canvas-side));
        transform: skewX(24deg);
        transform-origin: left top;
      }

      .print-studio__frame.is-canvas .print-studio__mat {
        padding: 0;
        background: transparent;
        overflow: visible;
      }

      .print-studio__frame.is-canvas .print-studio__art {
        border: 1px solid rgba(32,34,41,.12);
        box-shadow: inset -5px 0 8px rgba(0,0,0,.035), inset 0 -5px 8px rgba(0,0,0,.035);
      }

      .print-studio__frame.is-floater {
        padding: var(--floater-rail);
        background: var(--frame);
        box-shadow: 0 25px 52px rgba(28,22,18,.30), inset 0 0 0 1px rgba(255,255,255,.20), inset 0 0 0 2px var(--frame-side);
      }

      .print-studio__frame.is-floater::before {
        background: linear-gradient(90deg, var(--frame-side), var(--frame));
      }

      .print-studio__frame.is-floater::after {
        background: linear-gradient(180deg, var(--frame), var(--frame-side));
      }

      .print-studio__frame.is-floater .print-studio__mat {
        padding: var(--floater-gap);
        background: #070707;
        box-shadow: inset 0 0 0 1px rgba(255,255,255,.08);
      }

      .print-studio__frame.is-floater .print-studio__art {
        border: 0;
        box-shadow: 0 7px 12px rgba(0,0,0,.30), inset -9px 0 14px rgba(0,0,0,.07), inset 0 -8px 12px rgba(0,0,0,.06);
      }

      .print-studio__swatch {
        min-height: 112px;
        align-content: center;
        gap: 8px;
      }

      .print-studio__swatch .print-studio__corner {
        width: 64px;
        height: 64px;
        border: 1px solid rgba(32,34,41,.18);
        background: #f8f5ee;
        box-shadow: 0 10px 22px rgba(28,22,18,.13);
        overflow: hidden;
      }

      .print-studio__swatch .print-studio__corner::before,
      .print-studio__swatch .print-studio__corner::after {
        background: linear-gradient(135deg, var(--swatch-light), var(--swatch) 52%, var(--swatch-dark));
        box-shadow: inset 0 0 0 1px rgba(255,255,255,.18), inset -3px -3px 8px rgba(0,0,0,.16);
      }

      .print-studio__swatch .print-studio__corner::before {
        width: 17px;
        height: 64px;
      }

      .print-studio__swatch .print-studio__corner::after {
        width: 64px;
        height: 17px;
      }

      .print-studio__swatch .print-studio__corner i {
        position: absolute;
        left: 17px;
        top: 17px;
        right: 8px;
        bottom: 8px;
        display: block;
        border: 1px solid rgba(32,34,41,.13);
        background: linear-gradient(135deg, rgba(217,95,61,.13), rgba(34,91,111,.12)), #fbfaf7;
      }

      .print-studio__swatch[data-frame-kind="floater"] .print-studio__corner {
        background: #070707;
      }

      .print-studio__swatch[data-frame-kind="floater"] .print-studio__corner i {
        left: 22px;
        top: 22px;
        right: 7px;
        bottom: 7px;
        border-color: rgba(0,0,0,.35);
        box-shadow: 0 0 0 5px #070707;
      }
    `;
    document.head.append(style);
  }

  function frameColourMeta(name) {
    const map = {
      Black: { base: "#171717", light: "#343434", dark: "#050505" },
      White: { base: "#f7f4ed", light: "#ffffff", dark: "#d6d0c6" },
      Oak: { base: "#b88755", light: "#d5ad7d", dark: "#775132" },
      Gold: { base: "#c89b3f", light: "#f0cf78", dark: "#7d5a1d" },
    };
    return map[name] || map.Black;
  }

  function enhanceFrameSwatches(studio) {
    const kind = studio.material && studio.material.type === "canvas" && studio.frameMode === "floater" ? "floater" : "stock";
    qsa(studio, "[data-frame-colours] .print-studio__swatch").forEach((label) => {
      const text = label.textContent.trim();
      const meta = frameColourMeta(text);
      label.setAttribute("data-frame-kind", kind);
      label.setAttribute("data-frame-colour", text.toLowerCase());

      const corner = qs(label, ".print-studio__corner");
      if (!corner) return;
      corner.style.setProperty("--swatch", meta.base);
      corner.style.setProperty("--swatch-light", meta.light);
      corner.style.setProperty("--swatch-dark", meta.dark);
      if (!qs(corner, "i")) corner.append(document.createElement("i"));
    });
  }

  function enhancePreviewClasses(studio) {
    ensureFrameVisualStyles();

    const frame = qs(studio, "[data-frame]");
    if (!frame || !studio.material) return;

    const isCanvas = studio.material.type === "canvas";
    const isFloater = isCanvas && studio.frameMode === "floater";
    const isStock = studio.frameMode === "stock";

    frame.classList.toggle("is-canvas", isCanvas);
    frame.classList.toggle("is-floater", isFloater);
    frame.classList.toggle("is-stock-frame", isStock);
    frame.classList.toggle("is-paper-print", !isCanvas);

    const colour = frameColourMeta(studio.frameColour);
    studio.style.setProperty("--frame", colour.base);
    studio.style.setProperty("--frame-side", colour.dark);
    studio.style.setProperty("--canvas-edge", studio.canvasDepth === "1 1/2 inch" ? "#c0b6aa" : "#d5cec5");
    studio.style.setProperty("--canvas-side", studio.canvasDepth === "1 1/2 inch" ? "#a99d92" : "#bfb5aa");
  }

  function syncOrientationInputs(studio) {
    const form = studio.form || qs(studio, "[data-form]");
    if (!form) return;

    let orientationInput = qs(studio, '[data-prop="orientation"]');
    if (!orientationInput) {
      orientationInput = document.createElement("input");
      orientationInput.type = "hidden";
      orientationInput.setAttribute("data-prop", "orientation");
      form.append(orientationInput);
    }
    orientationInput.name = "properties[Orientation]";
    orientationInput.value = orientationLabel(studio);

    const sizeInput = qs(studio, '[data-prop="size"]');
    if (sizeInput) sizeInput.value = displaySize(studio, studio.size);
  }

  function updateStepLabels(studio) {
    const reviewTab = qs(studio, '[data-step-tab="4"]');
    if (reviewTab) {
      reviewTab.textContent = "Review";
      reviewTab.hidden = false;
      reviewTab.removeAttribute("aria-hidden");
      reviewTab.removeAttribute("tabindex");
    }
    const reviewPanel = qs(studio, '[data-step="4"]');
    if (reviewPanel) {
      reviewPanel.hidden = false;
      reviewPanel.removeAttribute("aria-hidden");
    }
  }

  function simplifyCheckoutFlow(studio) {
    updateStepLabels(studio);
    ensureMobileProgress(studio);
    ensureMobileNavigation(studio);
    if (studio.step > mobileStepCount()) studio.step = mobileStepCount();

    if (isMobileBuilder()) {
      qsa(studio, "[data-step]").forEach((panel) => {
        const step = Number(panel.dataset.step);
        const mobileStep = step;
        panel.hidden = false;
        panel.removeAttribute("aria-hidden");
        panel.classList.toggle("is-active", mobileStep === studio.step);
      });

      qsa(studio, "[data-mobile-step]").forEach((panel) => {
        panel.hidden = false;
        panel.removeAttribute("aria-hidden");
        panel.classList.toggle("is-active", Number(panel.getAttribute("data-mobile-step")) === studio.step);
      });

      qsa(studio, "[data-mobile-step-tab]").forEach((button) => {
        button.classList.toggle("is-active", Number(button.getAttribute("data-mobile-step-tab")) === studio.step);
      });

      studio.classList.toggle("is-mobile-review", studio.step === 4);

      const prev = qs(studio, "[data-prev]");
      const next = qs(studio, "[data-next]");
      const submit = qs(studio, "[data-submit]");
      if (prev) prev.disabled = studio.step === 1;
      if (next) next.hidden = studio.step >= 4;
      if (submit) submit.hidden = studio.step < 4;
      return;
    }

    if (studio.step > 4) studio.step = 4;
    studio.classList.remove("is-mobile-review");

    qsa(studio, "[data-step]").forEach((panel) => {
      const step = Number(panel.dataset.step);
      panel.hidden = false;
      panel.removeAttribute("aria-hidden");
      panel.classList.toggle("is-active", step === studio.step);
    });

    qsa(studio, "[data-mobile-step]").forEach((panel) => {
      panel.classList.remove("is-active");
    });

    qsa(studio, "[data-step-tab]").forEach((button) => {
      const step = Number(button.dataset.stepTab);
      if (step === 4) button.textContent = "Review";
      button.hidden = false;
      button.removeAttribute("aria-hidden");
      button.removeAttribute("tabindex");
      button.classList.toggle("is-active", step === studio.step);
    });

    const prev = qs(studio, "[data-prev]");
    const next = qs(studio, "[data-next]");
    const submit = qs(studio, "[data-submit]");
    if (prev) prev.disabled = studio.step === 1;
    if (next) next.hidden = studio.step >= 4;
    if (submit) submit.hidden = studio.step < 4;
  }

  function mobileGo(studio, step) {
    studio.step = Math.max(1, Math.min(mobileStepCount(), Number(step) || 1));
    simplifyCheckoutFlow(studio);
    focusMobileStep(studio);
  }

  function focusMobileStep(studio) {
    if (!studio.__mobileFlowReady || !window.matchMedia || !window.matchMedia("(max-width: 700px)").matches) return;
    const target = qs(studio, ".print-studio__steps") || qs(studio, `[data-step="${studio.step}"]`);
    if (!target) return;
    window.setTimeout(() => {
      const scene = qs(studio, ".print-studio__scene");
      const offset = scene ? scene.getBoundingClientRect().height + 8 : 8;
      const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, 40);
  }

  function isMobileBuilder() {
    return Boolean(window.matchMedia && window.matchMedia("(max-width: 700px)").matches);
  }

  function mobileStepCount() {
    return 4;
  }

  function mobileProductTypes(studio) {
    const hasPaper = studio.materials && studio.materials.some((material) => material.type === "photo" || material.type === "fine-art");
    const hasCanvas = studio.materials && studio.materials.some((material) => material.type === "canvas");
    const hasStock = studio.hasVariants && (studio.hasVariants(studio.products.stockFrame) || studio.hasVariants(studio.products.fineArtStockFrame));
    const hasFloater = studio.hasVariants && (studio.hasVariants(studio.products.matteFloater) || studio.hasVariants(studio.products.satinFloater));
    return [
      { key: "print-only", label: "Paper print only", note: "Photo and fine art paper prints without a frame", enabled: hasPaper },
      { key: "print-frame", label: "Paper print + stock frame", note: "Paper sizes that fit ready-made frames", enabled: hasPaper && hasStock, badge: "Most Popular" },
      { key: "canvas", label: "Canvas only", note: "Wrapped canvas prints without a floater frame", enabled: hasCanvas },
      { key: "canvas-floater", label: "Canvas + floater frame", note: "Canvas sizes that can pair with floater frames", enabled: hasCanvas && hasFloater, badge: "Most Popular" },
    ].filter((option) => option.enabled);
  }

  function mobileTypeFromState(studio) {
    if (studio.material && studio.material.type === "canvas") return studio.frameMode === "floater" ? "canvas-floater" : "canvas";
    return studio.frameMode === "stock" ? "print-frame" : "print-only";
  }

  function defaultMaterialForMobileType(studio, type) {
    const materials = studio.materials || [];
    if (type === "canvas" || type === "canvas-floater") {
      if (studio.material && studio.material.type === "canvas") return studio.material;
      return materials.find((material) => material.key === "matte-canvas") || materials.find((material) => material.type === "canvas") || studio.material;
    }
    if (studio.material && (studio.material.type === "photo" || studio.material.type === "fine-art")) return studio.material;
    return materials.find((material) => material.type === "photo") || materials.find((material) => material.type === "fine-art") || studio.material;
  }

  function frameModeForMobileType(type) {
    if (type === "print-frame") return "stock";
    if (type === "canvas-floater") return "floater";
    if (type === "canvas") return "canvas-only";
    return "none";
  }

  function ensureMobileProductType(studio) {
    const options = mobileProductTypes(studio);
    if (!options.length) return "";
    if (!options.some((option) => option.key === studio.mobileProductType)) studio.mobileProductType = mobileTypeFromState(studio);
    if (!options.some((option) => option.key === studio.mobileProductType)) studio.mobileProductType = options[0].key;
    return studio.mobileProductType;
  }

  function setMobileProductType(studio, type) {
    studio.mobileProductType = type;
    const material = defaultMaterialForMobileType(studio, type);
    if (material) studio.material = material;
    studio.frameMode = frameModeForMobileType(type);
    studio.paperFinish = studio.defaultPaperFinish ? studio.defaultPaperFinish() : studio.paperFinish;
    if (typeof studio.renderMaterials === "function") studio.renderMaterials();
    if (typeof studio.renderFinishOptions === "function") studio.renderFinishOptions();
    if (typeof studio.renderSizes === "function") studio.renderSizes();
    if (typeof studio.sync === "function") studio.sync();
    updateMobileProductType(studio);
  }

  function ensureMobileProductTypePanel(studio) {
    const form = studio.form || qs(studio, "[data-form]");
    const sizePanel = qs(studio, '[data-step="2"]');
    if (!form || !sizePanel) return null;

    let panel = qs(studio, "[data-mobile-product-type-panel]");
    if (!panel) {
      panel = document.createElement("section");
      panel.className = "print-studio__panel print-studio__mobile-type-panel";
      panel.setAttribute("data-mobile-product-type-panel", "");
      panel.setAttribute("data-mobile-step", "2");
      panel.innerHTML = '<h2>Choose product type</h2><p>Select the finished product first so the next size list is clean.</p><div class="print-studio__mobile-type-grid" data-mobile-product-types></div>';
      form.insertBefore(panel, sizePanel);
    }
    return panel;
  }

  function ensureMobileCropPanel(studio) {
    const uploadPanel = qs(studio, '[data-step="1"]');
    if (!uploadPanel) return null;
    let panel = qs(studio, "[data-mobile-crop-panel]");
    if (!panel) {
      panel = document.createElement("div");
      panel.className = "print-studio__mobile-crop";
      panel.setAttribute("data-mobile-crop-panel", "");
      uploadPanel.append(panel);
    }
    return panel;
  }

  function placePreviewControls(studio) {
    const scene = qs(studio, ".print-studio__scene");
    const controls = qs(studio, ".print-studio__preview-controls");
    if (!scene || !controls) return;
    if (isMobileBuilder()) {
      const cropPanel = ensureMobileCropPanel(studio);
      if (cropPanel && controls.parentNode !== cropPanel) cropPanel.append(controls);
    } else if (controls.parentNode !== scene) {
      scene.append(controls);
    }
    installCropControls(studio);
  }

  function ensureMobileProgress(studio) {
    const steps = qs(studio, ".print-studio__steps");
    if (!steps) return null;
    let progress = qs(studio, "[data-mobile-progress]");
    if (!progress) {
      progress = document.createElement("div");
      progress.className = "print-studio__mobile-progress";
      progress.setAttribute("data-mobile-progress", "");
      steps.after(progress);
    }
    const labels = ["Upload", "Size", "Material", "Review"];
    progress.innerHTML = labels.map((label, index) => '<button type="button" data-mobile-step-tab="' + (index + 1) + '">' + label + "</button>").join("");
    qsa(progress, "[data-mobile-step-tab]").forEach((button) => {
      button.addEventListener("click", () => {
        if (isMobileBuilder()) mobileGo(studio, Number(button.getAttribute("data-mobile-step-tab")) || 1);
        else if (typeof studio.go === "function") studio.go(Number(button.getAttribute("data-mobile-step-tab")) || 1);
      });
    });
    return progress;
  }

  function ensureMobileNavigation(studio) {
    if (studio.__mobileNavigationBound) return;
    const prev = qs(studio, "[data-prev]");
    const next = qs(studio, "[data-next]");
    if (prev) {
      prev.addEventListener("click", (event) => {
        if (!isMobileBuilder()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        mobileGo(studio, (Number(studio.step) || 1) - 1);
      }, true);
    }
    if (next) {
      next.addEventListener("click", (event) => {
        if (!isMobileBuilder()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        mobileGo(studio, (Number(studio.step) || 1) + 1);
      }, true);
    }
    studio.__mobileNavigationBound = true;
    studio.setAttribute("data-mobile-nav-bound", "true");
  }

  function installMobileDelegatedNavigation() {
    if (document.documentElement.dataset.printStudioMobileDelegatedNav) return;
    document.documentElement.dataset.printStudioMobileDelegatedNav = "true";
    document.addEventListener("click", (event) => {
      if (!isMobileBuilder()) return;
      const control = event.target && event.target.closest ? event.target.closest("[data-next], [data-prev], [data-mobile-step-tab]") : null;
      if (!control) return;
      const studio = control.closest("print-frame-studio");
      if (!studio) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const current = Number(studio.step) || 1;
      if (control.hasAttribute("data-next")) mobileGo(studio, current + 1);
      else if (control.hasAttribute("data-prev")) mobileGo(studio, current - 1);
      else mobileGo(studio, Number(control.getAttribute("data-mobile-step-tab")) || 1);
    }, true);
  }

  function updateMobileProductType(studio) {
    const type = ensureMobileProductType(studio);
    const panel = ensureMobileProductTypePanel(studio);
    const grid = panel && qs(panel, "[data-mobile-product-types]");
    if (grid) {
      grid.innerHTML = mobileProductTypes(studio).map((option) => (
        '<button type="button" class="print-studio__mobile-type' + (option.key === type ? " is-picked" : "") + '" data-mobile-product-type="' + option.key + '"><strong>' + option.label + "</strong>" + (option.badge ? '<em class="print-studio__popular-badge">' + option.badge + "</em>" : "") + "<small>" + option.note + "</small></button>"
      )).join("");
      qsa(grid, "[data-mobile-product-type]").forEach((button) => {
        button.addEventListener("click", () => setMobileProductType(studio, button.getAttribute("data-mobile-product-type")));
      });
    }

    qsa(studio, "[data-mobile-product-type]").forEach((button) => {
      button.classList.toggle("is-picked", button.getAttribute("data-mobile-product-type") === type);
    });
    updateMobileMaterialFilter(studio);
  }

  function updateMobileMaterialFilter(studio) {
    qsa(studio, "[data-materials] .print-studio__choice").forEach((label, index) => {
      label.classList.remove("is-mobile-incompatible");
      label.setAttribute("data-mobile-material-compatible", "true");
    });
  }

  function customerMaterialLabel(studio) {
    if (studio.material && studio.material.type === "fine-art") return "Fine art paper";
    return studio.material ? studio.material.label : "";
  }

  function customerPaperFinishLabel(studio) {
    if (!studio.material || studio.material.type !== "fine-art") return studio.paperFinish || "";

    const finish = String(studio.paperFinish || "").toLowerCase();
    if (finish.includes("textured")) return "Fine art textured";
    if (finish.includes("smooth")) return "Fine art smooth";
    return studio.paperFinish || "";
  }

  function syncCustomerDisplayProperties(studio) {
    if (typeof studio.setProp !== "function") return;
    studio.setProp("material", "Selected material", customerMaterialLabel(studio), Boolean(studio.material));
    studio.setProp("paperFinish", "Paper finish", customerPaperFinishLabel(studio), Boolean(studio.showPaperFinishes && studio.showPaperFinishes()));
  }

  function syncCustomerOrderNote(studio) {
    const note = qs(studio, "[data-customer-order-note]");
    if (!note) return;

    const value = note.value.trim();
    if (value) note.name = "properties[Order notes]";
    else note.removeAttribute("name");
  }

  function installCustomerOrderNote(studio) {
    const review = qs(studio, "[data-review]");
    if (!review || qs(studio, "[data-customer-order-note-wrap]")) return;

    const wrapper = document.createElement("label");
    wrapper.className = "print-studio__customer-note";
    wrapper.setAttribute("data-customer-order-note-wrap", "");
    wrapper.innerHTML = '<span>Order notes (optional)</span><textarea data-customer-order-note maxlength="500" placeholder="Add any notes about cropping, borders, colour, or production."></textarea><small>These notes will be attached to this print order.</small>';
    review.insertAdjacentElement("afterend", wrapper);

    const note = qs(wrapper, "[data-customer-order-note]");
    if (note) {
      note.addEventListener("input", () => syncCustomerOrderNote(studio));
      note.addEventListener("change", () => syncCustomerOrderNote(studio));
    }
  }

  function updateSizeLabels(studio) {
    qsa(studio, "[data-sizes] .print-studio__size").forEach((label) => {
      const strong = qs(label, "strong");
      if (!strong) return;
      const baseSize = label.getAttribute("data-base-size") || strong.textContent.trim();
      label.setAttribute("data-base-size", baseSize);
      strong.textContent = displaySize(studio, baseSize);
    });
  }

  function updateOrientationPicked(studio) {
    qsa(studio, "[data-orientation-option]").forEach((button) => {
      button.classList.toggle("is-picked", button.getAttribute("data-orientation-option") === orientationValue(studio));
    });
  }

  function cropNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function cropModeValue(studio) {
    return ["fill", "border-1", "border-2"].includes(studio.cropMode) ? studio.cropMode : "fit";
  }

  function cropModeLabel(studio) {
    const mode = cropModeValue(studio);
    if (mode === "fill") return "Fill and crop";
    if (mode === "border-1") return "1 inch border preset";
    if (mode === "border-2") return "2 inch border preset";
    return "Fit full image";
  }

  function borderPresetInches(mode) {
    if (mode === "border-1") return 1;
    if (mode === "border-2") return 2;
    return 0;
  }

  function borderPresetSideInches(widthInches, borderInches) {
    const maxComfortableBorder = widthInches * 0.12;
    const maxPhysicalBorder = Math.max(0, widthInches / 2 - 0.05);
    return Math.min(borderInches, maxComfortableBorder, maxPhysicalBorder);
  }

  function artworkRatio(studio) {
    const storedRatio = Number(studio.artRatio);
    if (Number.isFinite(storedRatio) && storedRatio > 0) return storedRatio;

    const image = qs(studio, "[data-art-image]");
    if (image && image.naturalWidth && image.naturalHeight) return image.naturalWidth / image.naturalHeight;

    const parts = sizeParts(displaySize(studio, studio.size));
    return parts ? parts[0] / parts[1] : 1;
  }

  function borderPresetGeometry(studio) {
    const mode = cropModeValue(studio);
    const borderInches = borderPresetInches(mode);
    const parts = sizeParts(displaySize(studio, studio.size));
    if (!borderInches || !parts) return { x: 0, y: 0, width: 100, height: 100 };

    const widthInches = parts[0];
    const heightInches = parts[1];
    const ratio = artworkRatio(studio);
    const sideBorder = borderPresetSideInches(widthInches, borderInches);

    let imageWidth = Math.max(0.1, widthInches - sideBorder * 2);
    let imageHeight = imageWidth / ratio;
    let x = sideBorder;
    let y = (heightInches - imageHeight) / 2;

    if (y < 0) {
      imageHeight = heightInches;
      imageWidth = imageHeight * ratio;
      x = Math.max(0, (widthInches - imageWidth) / 2);
      y = 0;
    }

    return {
      x: x / widthInches * 100,
      y: y / heightInches * 100,
      width: imageWidth / widthInches * 100,
      height: imageHeight / heightInches * 100,
    };
  }

  function borderPresetAppliedLabel(studio) {
    const mode = cropModeValue(studio);
    const borderInches = borderPresetInches(mode);
    const parts = sizeParts(displaySize(studio, studio.size));
    if (!borderInches || !parts) return "";

    const sideBorder = borderPresetSideInches(parts[0], borderInches);
    const rounded = Math.round(sideBorder * 100) / 100;
    return rounded + " inch side border";
  }

  function inchLabel(value) {
    return value === 1 ? "1 inch" : value + " inches";
  }

  function borderPresetCustomerNote(studio) {
    const mode = cropModeValue(studio);
    const borderInches = borderPresetInches(mode);
    const parts = sizeParts(displaySize(studio, studio.size));
    if (!borderInches || !parts) return "";

    const sideBorder = borderPresetSideInches(parts[0], borderInches);
    if (Math.abs(sideBorder - borderInches) < 0.05) {
      return "Side borders are set to " + inchLabel(borderInches) + " within your selected print size. Top and bottom spacing may vary to keep your artwork proportional.";
    }

    return "For this size, the border is scaled proportionally within your selected print size so your artwork stays balanced. Top and bottom spacing may vary.";
  }

  function updateBorderNote(studio) {
    const note = qs(studio, "[data-border-note]");
    if (!note) return;

    const text = borderPresetCustomerNote(studio);
    note.textContent = text;
    note.hidden = !text;
  }

  function percentValue(value) {
    return (Math.round(value * 1000) / 1000) + "%";
  }

  function cropBorderLabel(studio) {
    const mode = cropModeValue(studio);
    if (mode === "border-1") return "1 inch border preset";
    if (mode === "border-2") return "2 inch border preset";
    return "None";
  }

  function applyBorderVariables(studio) {
    const border = borderPresetGeometry(studio);
    studio.style.setProperty("--print-border-x", percentValue(border.x));
    studio.style.setProperty("--print-border-y", percentValue(border.y));
    studio.style.setProperty("--print-border-inner-w", percentValue(border.width));
    studio.style.setProperty("--print-border-inner-h", percentValue(border.height));
  }

  function syncCropInputs(studio) {
    const form = studio.form || qs(studio, "[data-form]");
    if (!form) return;
    applyBorderVariables(studio);
    updateBorderNote(studio);

    const mode = cropModeValue(studio);
    const borderPreset = borderPresetInches(mode) > 0;
    const values = [
      ["cropMode", "Crop mode", cropModeLabel(studio)],
      ["cropZoom", "Crop zoom", borderPreset ? "100%" : Math.round(cropNumber(studio.cropZoom, 100)) + "%"],
      ["cropX", "Crop horizontal", borderPreset ? "0%" : Math.round(cropNumber(studio.cropX, 0)) + "%"],
      ["cropY", "Crop vertical", borderPreset ? "0%" : Math.round(cropNumber(studio.cropY, 0)) + "%"],
      ["cropBorder", "Crop white border", cropBorderLabel(studio)],
      ["cropBorderApplied", "Crop side border applied", borderPresetAppliedLabel(studio)],
    ];

    values.forEach(([key, label, value]) => {
      let input = qs(studio, '[data-prop="' + key + '"]');
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.setAttribute("data-prop", key);
        form.append(input);
      }
      input.name = "properties[" + label + "]";
      input.value = value;
    });
  }

  function applyCropState(studio) {
    studio.cropZoom = cropNumber(studio.cropZoom, 100);
    studio.cropX = cropNumber(studio.cropX, 0);
    studio.cropY = cropNumber(studio.cropY, 0);
    studio.cropMode = cropModeValue(studio);

    studio.style.setProperty("--zoom", studio.cropZoom / 100);
    studio.style.setProperty("--crop-x", studio.cropX + "%");
    studio.style.setProperty("--crop-y", studio.cropY + "%");
    studio.setAttribute("data-crop-mode", studio.cropMode);

    const zoom = qs(studio, "[data-zoom]");
    const x = qs(studio, "[data-crop-x]");
    const y = qs(studio, "[data-crop-y]");
    if (zoom && Number(zoom.value) !== studio.cropZoom) zoom.value = String(studio.cropZoom);
    if (x && Number(x.value) !== studio.cropX) x.value = String(studio.cropX);
    if (y && Number(y.value) !== studio.cropY) y.value = String(studio.cropY);

    qsa(studio, "[data-crop-mode]").forEach((button) => {
      button.classList.toggle("is-picked", button.getAttribute("data-crop-mode") === studio.cropMode);
    });

    syncCropInputs(studio);
  }

  function installCropControls(studio) {
    const controls = qs(studio, ".print-studio__preview-controls");
    if (!controls) return;

    if (!studio.cropMode) studio.cropMode = "fit";
    if (!Number.isFinite(Number(studio.cropZoom))) {
      const zoom = qs(studio, "[data-zoom]");
      studio.cropZoom = zoom ? cropNumber(zoom.value, 100) : 100;
    }
    if (!Number.isFinite(Number(studio.cropX))) studio.cropX = 0;
    if (!Number.isFinite(Number(studio.cropY))) studio.cropY = 0;

    const zoom = qs(studio, "[data-zoom]");
    if (zoom && !zoom.dataset.cropEnhanced) {
      zoom.dataset.cropEnhanced = "true";
      zoom.addEventListener("input", () => {
        studio.cropZoom = cropNumber(zoom.value, 100);
        applyCropState(studio);
      });
    }

    if (!qs(studio, "[data-crop-x]")) {
      const xLabel = document.createElement("label");
      xLabel.innerHTML = 'Horizontal crop <input type="range" min="-45" max="45" value="0" data-crop-x>';
      controls.append(xLabel);
      qs(xLabel, "[data-crop-x]").addEventListener("input", (event) => {
        studio.cropX = cropNumber(event.target.value, 0);
        applyCropState(studio);
      });
    }

    if (!qs(studio, "[data-crop-y]")) {
      const yLabel = document.createElement("label");
      yLabel.innerHTML = 'Vertical crop <input type="range" min="-45" max="45" value="0" data-crop-y>';
      controls.append(yLabel);
      qs(yLabel, "[data-crop-y]").addEventListener("input", (event) => {
        studio.cropY = cropNumber(event.target.value, 0);
        applyCropState(studio);
      });
    }

    if (!qs(studio, "[data-crop-actions]")) {
      const actions = document.createElement("div");
      actions.className = "print-studio__crop-actions";
      actions.setAttribute("data-crop-actions", "");
      actions.innerHTML = [
        '<button type="button" class="print-studio__crop-mode" data-crop-mode="fit">Fit full image</button>',
        '<button type="button" class="print-studio__crop-mode" data-crop-mode="fill">Fill and crop</button>',
        '<button type="button" class="print-studio__crop-mode" data-crop-mode="border-1">1 inch border</button>',
        '<button type="button" class="print-studio__crop-mode" data-crop-mode="border-2">2 inch border</button>',
        '<button type="button" class="print-studio__crop-reset" data-crop-reset>Reset crop</button>',
        '<p class="print-studio__border-note" data-border-note role="status" hidden></p>',
      ].join("");
      controls.append(actions);
      qsa(actions, "[data-crop-mode]").forEach((button) => {
        button.addEventListener("click", () => {
          studio.cropMode = button.getAttribute("data-crop-mode") || "fit";
          applyCropState(studio);
        });
      });
      qs(actions, "[data-crop-reset]").addEventListener("click", () => {
        studio.cropMode = "fit";
        studio.cropZoom = 100;
        studio.cropX = 0;
        studio.cropY = 0;
        applyCropState(studio);
      });
    }

    applyCropState(studio);
  }

  function restorePreviewControls(studio) {
    placePreviewControls(studio);
  }

  function installOrientationControls(studio) {
    removeProofOption(studio);

    const panel = qs(studio, '[data-step="2"]');
    const sizes = qs(studio, "[data-sizes]");
    if (!panel || !sizes) return;
    if (!studio.printOrientation) studio.printOrientation = "portrait";

    let control = qs(studio, "[data-orientation-control]");
    if (!control) {
      control = document.createElement("div");
      control.className = "print-studio__orientation-control";
      control.setAttribute("data-orientation-control", "");
      control.innerHTML = '<span>Orientation</span><div class="print-studio__orientation-options"><button type="button" data-orientation-option="portrait">Portrait</button><button type="button" data-orientation-option="landscape">Landscape</button></div>';

      const filters = qs(studio, "[data-size-filters]");
      panel.insertBefore(control, filters || sizes);

      qsa(control, "[data-orientation-option]").forEach((button) => {
        button.addEventListener("click", () => {
          studio.printOrientation = button.getAttribute("data-orientation-option") || "portrait";
          updateOrientationPicked(studio);
          if (typeof studio.renderSizes === "function") studio.renderSizes();
          if (typeof studio.updatePreview === "function") studio.updatePreview();
          if (typeof studio.sync === "function") studio.sync();
        });
      });
    }

    updateOrientationPicked(studio);
    updateSizeLabels(studio);
    syncOrientationInputs(studio);
    updateStepLabels(studio);
  }

  function installOrderTools(studio) {
    removeProofOption(studio);

    const form = studio.form || qs(studio, "[data-form]");
    const summary = qs(studio, ".print-studio__summary");
    if (!form || !summary) return;

    let summaryMain = qs(summary, ".print-studio__summary-main");
    if (!summaryMain) {
      summaryMain = document.createElement("div");
      summaryMain.className = "print-studio__summary-main";
      while (summary.firstChild) summaryMain.append(summary.firstChild);
      summary.append(summaryMain);
    }

    let orderTools = qs(studio, "[data-order-tools]");
    if (!orderTools) {
      orderTools = document.createElement("div");
      orderTools.className = "print-studio__order-tools";
      orderTools.setAttribute("data-order-tools", "");
      form.insertBefore(orderTools, summary);
    }

    restorePreviewControls(studio);

    let quantity = qs(studio, "[data-quantity]") || qs(form, 'input[name="quantity"]');
    if (!quantity) {
      quantity = document.createElement("input");
      quantity.name = "quantity";
      quantity.value = "1";
    }

    quantity.type = "number";
    quantity.min = "1";
    quantity.step = "1";
    quantity.inputMode = "numeric";
    quantity.className = "print-studio__quantity-input";
    quantity.setAttribute("data-quantity", "");

    let quantityControl = qs(studio, "[data-quantity-control]");
    if (!quantityControl) {
      quantityControl = document.createElement("label");
      quantityControl.className = "print-studio__quantity-control";
      quantityControl.setAttribute("data-quantity-control", "");
      const quantityLabel = document.createElement("span");
      quantityLabel.textContent = "Quantity";
      quantityControl.append(quantityLabel);
    }

    if (quantity.parentNode !== quantityControl) quantityControl.append(quantity);
    if (quantityControl.parentNode !== summary) summary.append(quantityControl);

    if (!quantity.dataset.quantityBound) {
      quantity.dataset.quantityBound = "true";
      quantity.addEventListener("input", () => {
        if (typeof studio.sync === "function") studio.sync();
        else updateDisplayedPrice(studio, typeof studio.variant === "function" ? studio.variant() : null);
      });
      quantity.addEventListener("change", () => {
        if (quantityValue(studio) < 1) quantity.value = "1";
        if (typeof studio.sync === "function") studio.sync();
      });
    }
  }

  function patchStudioPrototype(Studio) {
    const proto = Studio && Studio.prototype;
    if (!proto || proto.__orderToolsPatched) return;

    proto.__orderToolsPatched = true;
    const originalSync = proto.sync;
    const originalInstallArtworkFit = proto.installArtworkFit;
    const originalRenderMaterials = proto.renderMaterials;
    const originalRenderSizes = proto.renderSizes;
    const originalRenderFinishOptions = proto.renderFinishOptions;
    const originalRenderFrameColours = proto.renderFrameColours;
    const originalFrameColours = proto.frameColours;
    const originalFrameModes = proto.frameModes;
    const originalUpdatePreview = proto.updatePreview;
    const originalRatio = proto.ratio;
    const originalSizeRatioNumber = proto.sizeRatioNumber;
    const originalGo = proto.go;
    const originalPreview = proto.preview;

    proto.quantityValue = function () {
      return quantityValue(this);
    };

    proto.priceForQuantity = function (variant) {
      return priceForQuantity(this, variant);
    };

    proto.updateDisplayedPrice = function (variant) {
      updateDisplayedPrice(this, variant);
    };

    proto.displaySize = function (size) {
      return displaySize(this, size);
    };

    proto.renderMaterials = function () {
      if (originalRenderMaterials) originalRenderMaterials.call(this);
      updateMobileMaterialFilter(this);
    };

    proto.renderSizes = function () {
      if (originalRenderSizes) originalRenderSizes.call(this);
      installOrientationControls(this);
      updateSizeLabels(this);
      removeProofOption(this);
    };

    proto.renderFinishOptions = function () {
      if (originalRenderFinishOptions) originalRenderFinishOptions.call(this);
      enhanceFrameSwatches(this);
      enhancePreviewClasses(this);
    };

    proto.renderFrameColours = function () {
      if (originalRenderFrameColours) originalRenderFrameColours.call(this);
      enhanceFrameSwatches(this);
    };

    proto.frameColours = function () {
      if (this.material && this.material.type === "canvas" && this.frameMode === "floater") return ["Black", "White", "Oak", "Gold"];
      return originalFrameColours ? originalFrameColours.call(this) : [];
    };

    proto.frameModes = function () {
      const modes = originalFrameModes ? originalFrameModes.call(this) : [];
      return modes.map((mode) => {
        if (mode.key !== "floater") return mode;
        return { key: mode.key, label: mode.label, note: "Black, white, oak, or gold floater frame" };
      });
    };

    proto.ratio = function (size) {
      const ratio = orientedRatio(this, size);
      if (ratio) return displaySize(this, size).replace("x", " / ");
      return originalRatio ? originalRatio.call(this, size) : "4 / 5";
    };

    proto.sizeRatioNumber = function (size) {
      const ratio = orientedRatio(this, size);
      return ratio || (originalSizeRatioNumber ? originalSizeRatioNumber.call(this, size) : 0);
    };

    proto.summaryText = function () {
      const parts = [customerMaterialLabel(this), displaySize(this, this.size)];
      if (this.showPaperFinishes()) parts.push(customerPaperFinishLabel(this));
      if (this.frameMode === "stock") parts.push(this.frameColour + " stock frame");
      if (this.frameMode === "floater") parts.push(this.frameColour + " floater frame", this.canvasDepth);
      if (this.material.type === "canvas" && this.frameMode === "canvas-only") parts.push(this.canvasDepth);
      if (this.frameMode === "none" && (this.material.type === "photo" || this.material.type === "fine-art")) parts.push("No frame");
      return parts.filter(Boolean).join(" - ");
    };

    proto.updateReview = function (variant) {
      const el = qs(this, "[data-review]");
      if (!el) return;

      const rows = [["Material", this.material ? customerMaterialLabel(this) : "Not selected"], ["Size", displaySize(this, this.size) || "Not selected"], ["Orientation", orientationLabel(this)]];
      if (this.showPaperFinishes()) rows.push(["Paper finish", customerPaperFinishLabel(this)]);
      if (this.frameMode === "none" && (this.material.type === "photo" || this.material.type === "fine-art")) rows.push(["Frame choice", "No frame"]);
      if (this.frameMode === "stock") rows.push(["Frame choice", "Stock frame"], ["Frame colour", this.frameColour]);
      if (this.material.type === "canvas") {
        rows.push(["Frame choice", this.frameMode === "floater" ? "Floater frame" : "Canvas print only"], ["Canvas depth", this.canvasDepth]);
        if (this.frameMode === "floater") rows.push(["Frame colour", this.frameColour]);
      }
      rows.push(["Quantity", String(quantityValue(this))], [quantityValue(this) > 1 ? "Selected total" : "Selected price", variant ? priceForQuantity(this, variant) : "Unavailable"]);

      el.innerHTML = rows.map((row) => '<div class="print-studio__review-row"><span>' + row[0] + "</span><strong>" + row[1] + "</strong></div>").join("");
    };

    proto.sync = function () {
      if (originalSync) originalSync.call(this);
      syncCustomerDisplayProperties(this);
      installCustomerOrderNote(this);
      syncCustomerOrderNote(this);
      removeProofOption(this);
      syncOrientationInputs(this);
      restorePreviewControls(this);
      applyCropState(this);
      enhanceFrameSwatches(this);
      enhancePreviewClasses(this);
      updateDisplayedPrice(this, typeof this.variant === "function" ? this.variant() : null);
    };

    proto.updatePreview = function () {
      if (originalUpdatePreview) originalUpdatePreview.call(this);
      enhancePreviewClasses(this);
    };

    proto.go = function (step) {
      const targetStep = Math.max(1, Math.min(mobileStepCount(), Number(step) || 1));
      if (originalGo) originalGo.call(this, Math.min(4, targetStep));
      else this.step = targetStep;
      this.step = targetStep;
      simplifyCheckoutFlow(this);
      focusMobileStep(this);
    };

    proto.preview = function (event) {
      if (originalPreview) originalPreview.call(this, event);

      const file = event && event.target && event.target.files && event.target.files[0];
      const image = qs(this, "[data-art-image]");
      if (!file || !image || !file.type || file.type.includes("tiff")) return;

      const apply = () => applyDetectedArtworkSize(this, file, image);
      if (image.complete && image.naturalWidth) apply();
      else image.addEventListener("load", apply, { once: true });
    };

    proto.installArtworkFit = function () {
      removeProofOption(this);
      installOrientationControls(this);
      updateStepLabels(this);
      installOrderTools(this);
      installCustomerOrderNote(this);
      ensureMobileNavigation(this);
      if (originalInstallArtworkFit) originalInstallArtworkFit.call(this);
      removeProofOption(this);
      installOrientationControls(this);
      updateStepLabels(this);
      installOrderTools(this);
      installCustomerOrderNote(this);
      ensureMobileNavigation(this);
      restorePreviewControls(this);
      simplifyCheckoutFlow(this);
      updateDisplayedPrice(this, typeof this.variant === "function" ? this.variant() : null);
    };
  }

  function init() {
    const Studio = customElements.get("print-frame-studio");
    ensureFrameVisualStyles();
    patchStudioPrototype(Studio);
    qsa(document, "print-frame-studio").forEach((studio) => {
      removeProofOption(studio);
      installOrientationControls(studio);
      updateStepLabels(studio);
      installOrderTools(studio);
      installCustomerOrderNote(studio);
      ensureMobileNavigation(studio);
      restorePreviewControls(studio);
      enhanceFrameSwatches(studio);
      enhancePreviewClasses(studio);
      simplifyCheckoutFlow(studio);
      studio.__mobileFlowReady = true;
      if (typeof studio.sync === "function") studio.sync();
    });
  }

  installMobileDelegatedNavigation();
  if (window.customElements) customElements.whenDefined("print-frame-studio").then(init);
})();
